import { useEffect, useState, useMemo } from "react";
import {
  listMaintenanceSchedules, listTools, createMaintenanceSchedule,
  logMaintenanceService, deleteMaintenanceSchedule,
} from "@/lib/firestore";
import { MNT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, Trash, Timer, Wrench, CheckCircle, Warning, Clock,
} from "@phosphor-icons/react";

function fmtDueLabel(s) {
  if (s.is_overdue) {
    const parts = [];
    if (s.time_overdue && s.days_remaining !== null) parts.push(`${Math.abs(s.days_remaining)}d overdue`);
    if (s.usage_overdue && s.uses_remaining !== null) parts.push(`${Math.abs(s.uses_remaining)} uses over`);
    return parts.join(" · ") || "Overdue";
  }
  const parts = [];
  if (s.days_remaining !== null && s.days_remaining !== undefined) parts.push(`${s.days_remaining}d`);
  if (s.uses_remaining !== null && s.uses_remaining !== undefined) parts.push(`${s.uses_remaining} uses`);
  return parts.length ? parts.join(" · ") + " left" : "—";
}

export default function Maintenance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [schedules, setSchedules] = useState([]);
  const [tools, setTools] = useState([]);
  const [filter, setFilter] = useState("all"); // all | overdue | due_soon
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    tool_id: "",
    name: "",
    description: "",
    interval_days: 30,
    interval_uses: 0,
    active: true,
  });
  const [showService, setShowService] = useState(null); // schedule
  const [serviceNotes, setServiceNotes] = useState("");

  const load = async () => {
    const [s, t] = await Promise.all([listMaintenanceSchedules(), listTools()]);
    setSchedules(s);
    setTools(t);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "overdue") return schedules.filter((s) => s.is_overdue);
    if (filter === "due_soon") return schedules.filter((s) => s.is_due_soon);
    return schedules;
  }, [filter, schedules]);

  const counts = useMemo(
    () => ({
      all: schedules.length,
      overdue: schedules.filter((s) => s.is_overdue).length,
      due_soon: schedules.filter((s) => s.is_due_soon).length,
    }),
    [schedules],
  );

  const openNew = () => {
    setForm({ tool_id: tools[0]?.id || "", name: "", description: "", interval_days: 30, interval_uses: 0, active: true });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.interval_days && !form.interval_uses) {
      toast.error("Set at least one interval (days or uses)");
      return;
    }
    try {
      await createMaintenanceSchedule({
        ...form,
        interval_days: parseInt(form.interval_days || 0),
        interval_uses: parseInt(form.interval_uses || 0),
      }, user);
      toast.success("Schedule created");
      setShowForm(false);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const service = async (e) => {
    e.preventDefault();
    try {
      await logMaintenanceService(showService.id, serviceNotes, user);
      toast.success("Service logged");
      setShowService(null);
      setServiceNotes("");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await deleteMaintenanceSchedule(id);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// PREVENTIVE MAINTENANCE</div>
          <h1 className="font-display font-black text-4xl">MAINTENANCE</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">
            {counts.overdue > 0 && (
              <span className="text-[#D32F2F] font-mono">{counts.overdue} overdue · </span>
            )}
            {counts.due_soon > 0 && (
              <span className="text-[#FFD700] font-mono">{counts.due_soon} due soon · </span>
            )}
            <span>{schedules.length} total schedules</span>
          </div>
        </div>
        {isAdmin && (
          <button
            data-testid={MNT.addBtn}
            onClick={openNew}
            className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] flex items-center gap-2 transition-colors duration-150"
          >
            <Plus size={16} weight="bold" /> New Schedule
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "all", label: "All", testId: MNT.filterAll, badge: counts.all },
          { k: "overdue", label: "Overdue", testId: MNT.filterOverdue, badge: counts.overdue, color: "#D32F2F" },
          { k: "due_soon", label: "Due Soon", testId: MNT.filterDueSoon, badge: counts.due_soon, color: "#FFD700" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={t.testId}
            className={`brutal-btn text-xs px-3 py-2 border transition-colors duration-150 flex items-center gap-2 ${
              filter === t.k
                ? "bg-[#FF4F00] text-black border-[#FF4F00]"
                : "border-[#27272A] hover:border-[#FF4F00]"
            }`}
          >
            {t.label}
            {t.badge > 0 && (
              <span
                className="font-mono text-[10px] px-1.5 py-0.5"
                style={{
                  background: filter === t.k ? "#0A0A0C" : (t.color || "#27272A"),
                  color: filter === t.k ? "#FF4F00" : (t.color === "#FFD700" ? "#0A0A0C" : "#F5F5F5"),
                }}
              >
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// NEW SCHEDULE</div>
                <h2 className="font-display font-black text-2xl">Preventive Maintenance</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <select
                data-testid={MNT.toolSelect}
                required
                value={form.tool_id}
                onChange={(e) => setForm({ ...form, tool_id: e.target.value })}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              >
                <option value="">Select tool...</option>
                {tools.map((t) => <option key={t.id} value={t.id}>{t.name} · #{t.tool_code}</option>)}
              </select>
              <input
                data-testid={MNT.nameInput}
                required
                placeholder="Task name (e.g. Monthly Calibration)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              />
              <textarea
                data-testid={MNT.descInput}
                rows={2}
                placeholder="Instructions / description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">
                    Every N days
                  </label>
                  <input
                    data-testid={MNT.daysInput}
                    type="number"
                    min="0"
                    value={form.interval_days}
                    onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                    className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">
                    Every N uses
                  </label>
                  <input
                    data-testid={MNT.usesInput}
                    type="number"
                    min="0"
                    value={form.interval_uses}
                    onChange={(e) => setForm({ ...form, interval_uses: e.target.value })}
                    className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]"
                  />
                </div>
              </div>
              <div className="font-mono text-[10px] text-[#52525B]">
                // Set 0 to disable that trigger. The first threshold hit fires the alert. //
              </div>
              <button
                type="submit"
                data-testid={MNT.saveBtn}
                className="brutal-btn w-full bg-[#FF4F00] text-black px-4 py-3 hover:bg-[#FF6B2B] transition-colors duration-150"
              >
                Create Schedule →
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Service modal */}
      {showService && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowService(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// LOG SERVICE</div>
                <h2 className="font-display font-black text-2xl">{showService.name}</h2>
                <div className="font-mono text-xs text-[#A1A1AA]">{showService.tool_name} · #{showService.tool_code}</div>
              </div>
              <button onClick={() => setShowService(null)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={service} className="space-y-3">
              <textarea
                data-testid={MNT.serviceNotesInput}
                rows={4}
                placeholder="Service notes (what was done, parts replaced, etc.)"
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              />
              <button
                type="submit"
                data-testid={MNT.serviceSubmit}
                className="brutal-btn w-full bg-[#00E676] text-black px-4 py-3 hover:opacity-90 transition-opacity duration-150"
              >
                <CheckCircle size={16} weight="bold" className="inline mr-1" /> Confirm Service Complete →
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Schedule list */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="brutal-card p-10 text-center text-[#52525B] font-mono">
            // {filter === "all" ? "no maintenance schedules yet — create one to track tool servicing" : `no schedules ${filter === "overdue" ? "overdue" : "due soon"}`} //
          </div>
        )}
        {filtered.map((s) => (
          <div
            key={s.id}
            data-testid={`schedule-${s.id}`}
            className={`brutal-card p-5 relative ${
              s.is_overdue ? "border-l-4 border-l-[#D32F2F]" : s.is_due_soon ? "border-l-4 border-l-[#FFD700]" : ""
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-3">
                {s.is_overdue ? (
                  <Warning size={20} weight="bold" className="text-[#D32F2F] mt-1" />
                ) : s.is_due_soon ? (
                  <Clock size={20} weight="bold" className="text-[#FFD700] mt-1" />
                ) : (
                  <Timer size={20} weight="bold" className="text-[#00E676] mt-1" />
                )}
                <div>
                  <div className="font-display font-bold text-lg">{s.name}</div>
                  <div className="font-mono text-xs text-[#A1A1AA]">
                    <Wrench size={10} className="inline mr-1" />
                    {s.tool_name || "—"} · #{s.tool_code || "—"}
                  </div>
                  {s.description && <div className="text-xs text-[#A1A1AA] mt-1 italic">{s.description}</div>}
                </div>
              </div>
              <div className="text-right">
                <div
                  className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                    s.is_overdue ? "border-[#D32F2F] text-[#D32F2F] bg-[#D32F2F]/10" :
                    s.is_due_soon ? "border-[#FFD700] text-[#FFD700] bg-[#FFD700]/10" :
                    "border-[#00E676] text-[#00E676]"
                  }`}
                >
                  {s.is_overdue ? "OVERDUE" : s.is_due_soon ? "DUE SOON" : "OK"}
                </div>
                <div className="font-mono text-xs text-[#F5F5F5] mt-1">{fmtDueLabel(s)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs border-t border-[#1a1a1e] pt-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Interval</div>
                <div className="font-mono text-[#F5F5F5]">
                  {s.interval_days ? `${s.interval_days}d` : "—"}
                  {s.interval_days && s.interval_uses ? " / " : ""}
                  {s.interval_uses ? `${s.interval_uses} uses` : ""}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Last Serviced</div>
                <div className="font-mono text-[#F5F5F5]">
                  {s.last_serviced_at ? new Date(s.last_serviced_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Uses Since</div>
                <div className="font-mono text-[#F5F5F5]">{s.uses_since_service || 0}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Serviced By</div>
                <div className="font-mono text-[#F5F5F5]">{s.last_serviced_by_name || "—"}</div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                data-testid={MNT.serviceBtn(s.id)}
                onClick={() => { setServiceNotes(""); setShowService(s); }}
                className="brutal-btn border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-4 py-2 text-xs flex items-center gap-1 transition-colors duration-150"
              >
                <CheckCircle size={12} weight="bold" /> Log Service
              </button>
              {isAdmin && (
                <button
                  data-testid={MNT.deleteBtn(s.id)}
                  onClick={() => del(s.id)}
                  className="brutal-btn border border-[#27272A] hover:border-[#D32F2F] hover:text-[#D32F2F] px-3 py-2 text-xs transition-colors duration-150"
                >
                  <Trash size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
